import type { SystemOptionConfigurationType } from 'data/types';
import type { ZoneConcealedDuctLayout } from 'utils/concealed-duct-geometry';
import { isConcealedDuctProduct } from 'utils/ducting-products';
import type { QuoteFloorplanSnapshot } from 'utils/quote-floorplan-snapshot';

export function concealedDuctZoneIdsForSystem(
  configuration: Pick<SystemOptionConfigurationType, 'condensers'>
): Set<string> {
  return new Set(
    configuration.condensers.flatMap((condenser) =>
      condenser.zones
        .filter((zone) => isConcealedDuctProduct(zone.selectedProduct))
        .map((zone) => zone.uniqueId)
    )
  );
}

export function keepDuctRunLayoutsForSystem(
  layouts: ZoneConcealedDuctLayout[],
  configuration: Pick<SystemOptionConfigurationType, 'condensers'>
): ZoneConcealedDuctLayout[] {
  const concealedZoneIds = concealedDuctZoneIdsForSystem(configuration);
  return layouts.filter(
    (layout) =>
      layout.zoneUniqueId !== undefined &&
      concealedZoneIds.has(layout.zoneUniqueId)
  );
}

export function withSystemDuctRuns(
  snapshot: QuoteFloorplanSnapshot,
  configuration: Pick<SystemOptionConfigurationType, 'condensers'>
): QuoteFloorplanSnapshot {
  const ductLayouts = snapshot.ductLayouts ?? [];
  if (ductLayouts.length === 0) return snapshot;

  const concealedZoneIds = concealedDuctZoneIdsForSystem(configuration);
  const hasStaleRuns = ductLayouts.some(
    (layout) =>
      layout.runs.length > 0 && !concealedZoneIds.has(layout.zoneUniqueId)
  );
  if (!hasStaleRuns) return snapshot;

  return {
    ...snapshot,
    ductLayouts: ductLayouts.map((layout) =>
      concealedZoneIds.has(layout.zoneUniqueId)
        ? layout
        : { ...layout, runs: [] }
    )
  };
}
